import { addMonths } from 'date-fns/addMonths';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval';
import { endOfMonth } from 'date-fns/endOfMonth';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isValid } from 'date-fns/isValid';
import { min } from 'date-fns/min';
import { parse } from 'date-fns/parse';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import {
  PurchaseOrderDashboardKpis,
  PurchaseOrderMissionCoverage,
  PurchaseOrderStatus,
} from '@bloomays-lib/types.shared';
import { isHoliday, isWeekEndDay } from '@bloomays-lib/utils.shared';
import { derivePurchaseOrderStatusForDate } from './missionPurchaseOrderStatus';

/** Extensions et attribut `accept` pour les sélecteurs d'upload BDC (PDF uniquement). */
export const PURCHASE_ORDER_PDF_FILE_TYPES = ['.pdf'];
export const PURCHASE_ORDER_PDF_ACCEPT = 'application/pdf,.pdf';

/**
 * Représentation front d'un BDC telle que renvoyée par la query GraphQL
 * `purchaseOrders` (inclut `recordId` et le `remainingAmountHT` dérivé côté API).
 */
export type DashboardPurchaseOrder = {
  recordId: string;
  orderNumber: string;
  missionsRecordId?: string[] | null;
  clientSocietyRecordId?: string | null;
  fileUrl?: string | null;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  amountHT?: number | null;
  invoicedAmountHT?: number | null;
  remainingAmountHT?: number | null;
  useForNonWorkingHours?: boolean | null;
  invoiceComment?: string | null;
  status: PurchaseOrderStatus;
};

/** Statut d'affichage : le badge « Partiel » est dérivé côté front. */
export type PurchaseOrderDisplayStatus = 'received' | 'partial' | 'waiting' | 'expired' | 'soldout';

export type PurchaseOrderDisplayStatusInput = Pick<
  DashboardPurchaseOrder,
  'status' | 'endDate' | 'amountHT' | 'invoicedAmountHT' | 'remainingAmountHT'
>;

/**
 * Montant restant : privilégie la valeur dérivée par l'API (`remainingAmountHT`),
 * sinon recalcule `amountHT − invoicedAmountHT`.
 */
export const computeRemainingAmount = (
  purchaseOrder: Pick<DashboardPurchaseOrder, 'amountHT' | 'invoicedAmountHT' | 'remainingAmountHT'>,
): number | undefined => {
  if (purchaseOrder.remainingAmountHT !== null && purchaseOrder.remainingAmountHT !== undefined) {
    return purchaseOrder.remainingAmountHT;
  }
  if (purchaseOrder.amountHT === null || purchaseOrder.amountHT === undefined) {
    return undefined;
  }
  return purchaseOrder.amountHT - (purchaseOrder.invoicedAmountHT ?? 0);
};

/**
 * Statut d'affichage : « Partiel » pour un BDC `received` partiellement consommé
 * (facturé > 0 et restant > 0). Le statut backend n'est jamais modifié.
 */
export const derivePurchaseOrderDisplayStatus = (
  purchaseOrder: Pick<DashboardPurchaseOrder, 'status' | 'amountHT' | 'invoicedAmountHT' | 'remainingAmountHT'>,
): PurchaseOrderDisplayStatus => {
  if (purchaseOrder.status === PurchaseOrderStatus.soldout) {
    return 'soldout';
  }
  if (purchaseOrder.status === PurchaseOrderStatus.expired) {
    return 'expired';
  }
  if (purchaseOrder.status === PurchaseOrderStatus.received) {
    const invoiced = purchaseOrder.invoicedAmountHT ?? 0;
    const remaining = computeRemainingAmount(purchaseOrder);
    if (invoiced > 0 && remaining !== undefined && remaining > 0) {
      return 'partial';
    }
    return 'received';
  }
  return 'waiting';
};

/**
 * Statut d'affichage avec recalcul date/montant (aligné fiche mission) puis badge dashboard
 * (inclut « Partiel » pour un BDC reçu partiellement consommé).
 */
export const derivePurchaseOrderDisplayStatusForReferenceDate = (
  purchaseOrder: PurchaseOrderDisplayStatusInput,
  referenceDate: Date = new Date(),
): PurchaseOrderDisplayStatus => {
  const effectiveStatus = derivePurchaseOrderStatusForDate(
    {
      status: purchaseOrder.status,
      amountHT: purchaseOrder.amountHT ?? undefined,
      invoicedAmountHT: purchaseOrder.invoicedAmountHT ?? undefined,
      endDate: purchaseOrder.endDate ? new Date(purchaseOrder.endDate) : undefined,
    },
    referenceDate,
  );
  return derivePurchaseOrderDisplayStatus({ ...purchaseOrder, status: effectiveStatus });
};

export type PurchaseOrderAmountsAggregate = {
  engaged: number;
  remaining: number;
  consumedRatio: number;
};

/** Agrège les montants sur l'ensemble des BDC fournis (tous les BDC, cf. clarification I1). */
export const aggregatePurchaseOrderAmounts = (
  purchaseOrders: ReadonlyArray<Pick<DashboardPurchaseOrder, 'amountHT' | 'invoicedAmountHT'>>,
): PurchaseOrderAmountsAggregate => {
  let engaged = 0;
  let remaining = 0;
  purchaseOrders.forEach((purchaseOrder) => {
    if (purchaseOrder.amountHT !== null && purchaseOrder.amountHT !== undefined) {
      engaged += purchaseOrder.amountHT;
      remaining += purchaseOrder.amountHT - (purchaseOrder.invoicedAmountHT ?? 0);
    }
  });
  const consumedRatio = engaged > 0 ? (engaged - remaining) / engaged : 0;
  return { engaged, remaining, consumedRatio };
};

export type MissingMonthKey = 'monthMinus1' | 'current' | 'monthPlus1' | 'monthPlus2' | 'monthPlus3';
export type MissingUrgency = 'urgent' | 'anticipate' | 'plan';

export const MONTH_OFFSET_FOR_KEY: Record<MissingMonthKey, number> = {
  monthMinus1: -1,
  current: 0,
  monthPlus1: 1,
  monthPlus2: 2,
  monthPlus3: 3,
};

/** Clé de mois par décalage, pour relire les mois non couverts renvoyés par l'API. */
const MISSING_MONTH_KEY_BY_OFFSET = new Map<number, MissingMonthKey>(
  (Object.entries(MONTH_OFFSET_FOR_KEY) as [MissingMonthKey, number][]).map(([key, offset]) => [offset, key]),
);

/**
 * Un mois déjà écoulé ou en cours sans BDC bloque la facturation : il est aussi urgent
 * que le mois prochain. Au-delà, on reste sur de l'anticipation.
 */
const URGENCY_FOR_MONTH: Record<MissingMonthKey, MissingUrgency> = {
  monthMinus1: 'urgent',
  current: 'urgent',
  monthPlus1: 'urgent',
  monthPlus2: 'anticipate',
  monthPlus3: 'plan',
};

export type MissingPurchaseOrderRow = {
  id: string;
  missionRecordId: string;
  missionLabel: string;
  clientSocietyName: string;
  missingMonth: MissingMonthKey;
  urgency: MissingUrgency;
};

/**
 * Construit une ligne par mission × mois à couvrir (M-1 → M+3) à partir des mois sans
 * BDC remontés par l'API. Une mission peut donc apparaître plusieurs fois ; les mois où
 * elle est inactive sont déjà exclus côté API.
 */
export const buildMissingRows = (coverages: ReadonlyArray<PurchaseOrderMissionCoverage>): MissingPurchaseOrderRow[] => {
  const rows: MissingPurchaseOrderRow[] = [];
  coverages.forEach((coverage) => {
    coverage.monthsWithoutPurchaseOrder.forEach((monthOffset) => {
      const key = MISSING_MONTH_KEY_BY_OFFSET.get(monthOffset);
      if (!key) {
        return;
      }
      rows.push({
        id: `${coverage.missionRecordId}-${key}`,
        missionRecordId: coverage.missionRecordId,
        missionLabel: coverage.missionLabel ?? '',
        clientSocietyName: coverage.clientSocietyName ?? '',
        missingMonth: key,
        urgency: URGENCY_FOR_MONTH[key],
      });
    });
  });
  return rows;
};

/**
 * Nombre de jours ouvrés en France (hors week-ends et jours fériés) dans le mois de `date`.
 * NB : `isWeekEndDay` de `utils.shared` renvoie en réalité `true` pour un jour de semaine
 * (lundi→vendredi) et `false` le week-end — on garde donc les jours où elle vaut `true`.
 */
export const frenchBusinessDaysInMonth = (date: Date): number => {
  const days = eachDayOfInterval({ start: startOfMonth(date), end: endOfMonth(date) });
  return days.filter((day) => isWeekEndDay(day) && !isHoliday(day)).length;
};

/** Date de référence du mois cible (M+1/M+2/M+3) à partir de la date de référence du dashboard. */
export const monthDateForKey = (referenceDate: Date, key: MissingMonthKey): Date =>
  addMonths(referenceDate, MONTH_OFFSET_FOR_KEY[key]);

/** Estimation mensuelle = TJM × jours ouvrés en France du mois cible. */
export const monthlyEstimate = (tjm: number | undefined | null, monthDate: Date): number | undefined => {
  if (tjm === null || tjm === undefined) {
    return undefined;
  }
  return tjm * frenchBusinessDaysInMonth(monthDate);
};

export type MissionPricingResult = {
  startDate?: string | Date | null;
  salePricePerDayWithoutTaxes?: number | null;
};

/** Élément renvoyé par la requête GraphQL dédiée d'enrichissement mission. */
export type MissionEnrichmentResult = {
  recordId: string;
  label?: string | null;
  missionCode?: string | null;
  beginMission?: string | null;
  endMission?: string | null;
  clientSocietyName?: string[] | null;
  bloomerFullName?: string[] | null;
  hoursPerDays?: number | null;
  pricing?: MissionPricingResult[] | null;
  jobOwner?: { firstname: string; lastname: string } | null;
};

export type MissionEnrichment = {
  missionLabel?: string;
  /** Code mission repris dans les BDC client (trigramme) — critère de recherche du dashboard BDC. */
  missionCode?: string;
  tjm?: number;
  bloomerFullName?: string;
  clientSocietyName?: string;
  accountManager?: string;
  beginMission?: string;
  endMission?: string;
};

/**
 * Fenêtre affichée dans la vue couverture : le mois précédent (contrôle de clôture et de
 * facturation) jusqu'à M+2 (TEC-3263).
 */
export type CoverageMonthSlotKey = 'monthMinus1' | 'current' | 'monthPlus1' | 'monthPlus2';

export const COVERAGE_MONTH_OFFSET: Record<CoverageMonthSlotKey, number> = {
  monthMinus1: -1,
  current: 0,
  monthPlus1: 1,
  monthPlus2: 2,
};

export const COVERAGE_MONTH_SLOT_KEYS: CoverageMonthSlotKey[] = ['monthMinus1', 'current', 'monthPlus1', 'monthPlus2'];

export const COVERAGE_MONTH_OFFSETS: number[] = COVERAGE_MONTH_SLOT_KEYS.map((key) => COVERAGE_MONTH_OFFSET[key]);

const COVERAGE_EXPIRING_SOON_DAYS = 14;

export type MonthCoverageDetail =
  | { status: 'inactive' }
  | { status: 'covered'; orderNumber: string }
  | { status: 'notCovered' };

export type CoverageMonthSegmentState = 'inactive' | 'covered' | 'missing' | 'expiringSoon';

export const formatMissionPeriod = (start?: string, end?: string): string => {
  const formatDate = (value?: string) => (value ? convertDateToStringFormat(value, 'dd/MM/yyyy') : '—');
  return `${formatDate(start)} → ${formatDate(end)}`;
};

export const formatAccountManagerShort = (accountManager: string | undefined): string => {
  if (!accountManager) {
    return '—';
  }
  const parts = accountManager.trim().split(/\s+/);
  if (parts.length < 2) {
    return accountManager;
  }
  return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
};

export const accountManagerInitials = (accountManager: string | undefined): string => {
  if (!accountManager) {
    return '—';
  }
  const parts = accountManager.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '—';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const missionDateOverlapsInterval = (
  beginMission: Date,
  endMission: Date,
  intervalStart: Date,
  intervalEnd: Date,
): boolean => {
  const missionStart = startOfDay(beginMission);
  const missionEnd = startOfDay(endMission);
  const monthStart = startOfDay(intervalStart);
  const monthEnd = startOfDay(intervalEnd);
  return !isAfter(missionStart, monthEnd) && !isBefore(missionEnd, monthStart);
};

const parseMissionDate = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : null;
};

const isPurchaseOrderLinkedToMission = (purchaseOrder: DashboardPurchaseOrder, missionRecordId: string): boolean =>
  Array.isArray(purchaseOrder.missionsRecordId) && purchaseOrder.missionsRecordId.includes(missionRecordId);

/** Statut effectif d'un BDC à la date de référence (aligné sur la logique backend de couverture). */
export const deriveEffectivePurchaseOrderStatus = (
  purchaseOrder: Pick<DashboardPurchaseOrder, 'status' | 'endDate' | 'amountHT' | 'invoicedAmountHT'>,
  referenceDate: Date,
): PurchaseOrderStatus => {
  const invoicedAmountHT = purchaseOrder.invoicedAmountHT ?? 0;
  if (
    purchaseOrder.amountHT !== undefined &&
    purchaseOrder.amountHT !== null &&
    invoicedAmountHT >= purchaseOrder.amountHT
  ) {
    return PurchaseOrderStatus.soldout;
  }
  if (purchaseOrder.endDate && isBefore(startOfDay(purchaseOrder.endDate), startOfDay(referenceDate))) {
    return PurchaseOrderStatus.expired;
  }
  return purchaseOrder.status;
};

/**
 * Date à laquelle la validité d'un BDC est jugée pour le mois évalué : la date de
 * référence pour le mois en cours et les mois à venir, la fin du mois pour un mois déjà
 * écoulé. Sans cela, un BDC clos avec le mois précédent serait vu comme expiré et ce
 * mois apparaîtrait à tort comme non couvert.
 */
const statusReferenceDateForMonth = (monthEnd: Date, referenceDate: Date): Date => min([referenceDate, monthEnd]);

export const purchaseOrderCoversMissionMonth = (
  purchaseOrder: DashboardPurchaseOrder,
  missionRecordId: string,
  monthStart: Date,
  monthEnd: Date,
  referenceDate: Date,
): boolean => {
  if (!isPurchaseOrderLinkedToMission(purchaseOrder, missionRecordId)) {
    return false;
  }
  if (
    deriveEffectivePurchaseOrderStatus(purchaseOrder, statusReferenceDateForMonth(monthEnd, referenceDate)) !==
    PurchaseOrderStatus.received
  ) {
    return false;
  }
  if (purchaseOrder.startDate && isAfter(startOfDay(purchaseOrder.startDate), startOfDay(monthEnd))) {
    return false;
  }
  if (purchaseOrder.endDate && isBefore(startOfDay(purchaseOrder.endDate), startOfDay(monthStart))) {
    return false;
  }
  if (
    purchaseOrder.amountHT !== undefined &&
    purchaseOrder.amountHT !== null &&
    (purchaseOrder.invoicedAmountHT ?? 0) >= purchaseOrder.amountHT
  ) {
    return false;
  }
  return true;
};

const purchaseOrderStartTimestamp = (purchaseOrder: Pick<DashboardPurchaseOrder, 'startDate'>): number => {
  if (!purchaseOrder.startDate) {
    return Number.POSITIVE_INFINITY;
  }
  const timestamp = new Date(purchaseOrder.startDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

/** BDC sans date de début considérés comme les plus récents. */
export const sortPurchaseOrdersNewestFirst = (
  purchaseOrders: ReadonlyArray<DashboardPurchaseOrder>,
): DashboardPurchaseOrder[] =>
  [...purchaseOrders].sort((left, right) => purchaseOrderStartTimestamp(right) - purchaseOrderStartTimestamp(left));

export const purchaseOrdersLinkedToMission = (
  purchaseOrders: ReadonlyArray<DashboardPurchaseOrder>,
  missionRecordId: string,
): DashboardPurchaseOrder[] =>
  sortPurchaseOrdersNewestFirst(
    purchaseOrders.filter((purchaseOrder) => isPurchaseOrderLinkedToMission(purchaseOrder, missionRecordId)),
  );

export const findCoveringPurchaseOrderForMonth = (
  purchaseOrders: ReadonlyArray<DashboardPurchaseOrder>,
  missionRecordId: string,
  monthStart: Date,
  monthEnd: Date,
  referenceDate: Date,
): DashboardPurchaseOrder | undefined =>
  purchaseOrders.find((purchaseOrder) =>
    purchaseOrderCoversMissionMonth(purchaseOrder, missionRecordId, monthStart, monthEnd, referenceDate),
  );

export const resolveMonthCoverageDetail = (params: {
  missionRecordId: string;
  beginMission?: string | null;
  endMission?: string | null;
  purchaseOrders: ReadonlyArray<DashboardPurchaseOrder>;
  referenceDate: Date;
  monthOffset: number;
}): MonthCoverageDetail => {
  const monthStart = startOfMonth(addMonths(params.referenceDate, params.monthOffset));
  const monthEnd = endOfMonth(monthStart);
  const begin = parseMissionDate(params.beginMission);
  const end = parseMissionDate(params.endMission);

  if (begin && end && !missionDateOverlapsInterval(begin, end, monthStart, monthEnd)) {
    return { status: 'inactive' };
  }

  const covering = findCoveringPurchaseOrderForMonth(
    params.purchaseOrders,
    params.missionRecordId,
    monthStart,
    monthEnd,
    params.referenceDate,
  );
  if (covering) {
    return { status: 'covered', orderNumber: covering.orderNumber };
  }

  return { status: 'notCovered' };
};

export const resolveCoverageMonthSegmentState = (params: {
  missionRecordId: string;
  beginMission?: string | null;
  endMission?: string | null;
  purchaseOrders: ReadonlyArray<DashboardPurchaseOrder>;
  referenceDate: Date;
  monthOffset: number;
}): CoverageMonthSegmentState => {
  const detail = resolveMonthCoverageDetail(params);
  if (detail.status === 'inactive') {
    return 'inactive';
  }
  if (detail.status === 'notCovered') {
    return 'missing';
  }

  const monthStart = startOfMonth(addMonths(params.referenceDate, params.monthOffset));
  const monthEnd = endOfMonth(monthStart);
  const covering = findCoveringPurchaseOrderForMonth(
    params.purchaseOrders,
    params.missionRecordId,
    monthStart,
    monthEnd,
    params.referenceDate,
  );
  const endDate = covering?.endDate ? parseMissionDate(String(covering.endDate)) : null;
  if (endDate) {
    const daysUntilExpiry = differenceInCalendarDays(endDate, startOfDay(params.referenceDate));
    if (daysUntilExpiry >= 0 && daysUntilExpiry <= COVERAGE_EXPIRING_SOON_DAYS) {
      return 'expiringSoon';
    }
  }

  return 'covered';
};

/** TJM courant = tarif de vente de la tarification la plus récente (par date de début). */
export const currentTjm = (pricing?: MissionPricingResult[] | null): number | undefined => {
  if (!pricing || pricing.length === 0) {
    return undefined;
  }
  const withSalePrice = pricing.filter(
    (entry) => entry.salePricePerDayWithoutTaxes !== null && entry.salePricePerDayWithoutTaxes !== undefined,
  );
  if (withSalePrice.length === 0) {
    return undefined;
  }
  const sorted = [...withSalePrice].sort(
    (left, right) => new Date(left.startDate ?? 0).getTime() - new Date(right.startDate ?? 0).getTime(),
  );
  return sorted[sorted.length - 1].salePricePerDayWithoutTaxes ?? undefined;
};

/** Construit la table d'enrichissement par `missionRecordId` (TJM, bloomer, client, AM). */
export const buildMissionEnrichmentMap = (
  results: ReadonlyArray<MissionEnrichmentResult>,
): Record<string, MissionEnrichment> => {
  const map: Record<string, MissionEnrichment> = {};
  results.forEach((result) => {
    map[result.recordId] = {
      missionLabel: result.label ?? undefined,
      missionCode: result.missionCode ?? undefined,
      tjm: currentTjm(result.pricing),
      bloomerFullName: result.bloomerFullName?.[0] ?? undefined,
      clientSocietyName: result.clientSocietyName?.[0] ?? undefined,
      accountManager: result.jobOwner ? `${result.jobOwner.firstname} ${result.jobOwner.lastname}`.trim() : undefined,
      beginMission: result.beginMission ?? undefined,
      endMission: result.endMission ?? undefined,
    };
  });
  return map;
};

export type CoveredKpi = {
  covered: number;
  total: number;
  gap: number;
};

/**
 * KPI « Missions couvertes » sur le dernier mois de la fenêtre de couverture (M+2) :
 * une mission est couverte si elle a un BDC `received` couvrant le mois (clarification
 * A1). Le total est l'ensemble du périmètre (received + waiting + missing) sur cet
 * horizon.
 */
export const computeCoveredHorizonKpi = (kpis: PurchaseOrderDashboardKpis): CoveredKpi => {
  const horizon = kpis.monthPlus2;
  const covered = horizon.received.count;
  const total = horizon.received.count + horizon.waiting.count + horizon.missing.count;
  return { covered, total, gap: total - covered };
};
